<template>
  <FilterOverview title="Aufgabenübersicht" :showFilterInitially="false" @reload="onReload">
    <template #toolbar-left>
      <b-button-group size="sm" class="ml-2">
        <b-dropdown size="sm" :variant="isMeineAnsicht ? 'primary' : ''">
          <template #button-content>
            <span v-if="mirZugewiesenCount > 0" class="ansicht-count-badge mr-1">
              {{ mirZugewiesenCount }}
            </span>
            {{ isMeineAnsicht ? ansicht : 'Meine' }}
          </template>
          <b-dropdown-item @click="changeFilterButton" id="Mir zugewiesen">Mir zugewiesen</b-dropdown-item>
          <b-dropdown-item @click="changeFilterButton" id="Von mir erstellt">
            Von mir erstellt
          </b-dropdown-item>
          <b-dropdown-item @click="changeFilterButton" id="Von mir beobachtet">
            Von mir beobachtet
          </b-dropdown-item>
        </b-dropdown>
        <b-dropdown size="sm" :variant="isTeamAnsicht ? 'primary' : ''">
          <template #button-content>
            <span v-if="teamAnsichtCount > 0" class="ansicht-count-badge mr-1">{{ teamAnsichtCount }}</span>
            {{ isTeamAnsicht ? ansicht : 'Team' }}
          </template>
          <b-dropdown-item @click="changeFilterButton" id="Team">Team</b-dropdown-item>
          <b-dropdown-item @click="changeFilterButton" id="Vom Team erstellt">
            Vom Team erstellt
          </b-dropdown-item>
          <b-dropdown-item @click="changeFilterButton" id="Team zugewiesen">Team zugewiesen</b-dropdown-item>
        </b-dropdown>
        <b-button
          size="sm"
          @click="changeFilterButton"
          id="Nicht zugewiesen"
          :variant="ansicht === 'Nicht zugewiesen' ? 'primary' : ''"
        >
          <span v-if="teamNotificationCount > 0" class="ansicht-count-badge mr-1">
            {{ teamNotificationCount }}
          </span>
          Nicht zugewiesen
        </b-button>
        <b-button
          size="sm"
          @click="changeFilterButton"
          id="Teamübergreifend"
          :variant="ansicht === 'Teamübergreifend' ? 'primary' : ''"
        >
          Teamübergreifend
        </b-button>
      </b-button-group>

      <div
        class="ml-3 d-flex"
        v-if="isAdmin && showFakeUserSelect"
        style="
          background-color: var(--primary);
          padding: 4px;
          border-radius: var(--radius-sm);
          color: white;
          white-space: nowrap;
          align-items: center;
        "
      >
        <span class="mr-2">Fake User:</span>
        <AppSelect
          v-model="fakeUser"
          :options="zugewiesenePersonOptions"
          label="Fake User"
          placeholder="Suchen..."
          no-results-text="Bearbeiter/in nicht gefunden"
          class="mr-2"
          style="flex-grow: 1; --app-select-height: 32px; --app-select-font-size: 14px"
        />
        <i
          v-if="fakeUser !== null"
          class="fas fa-times text-danger align-self-center ml-2"
          @click="fakeUser = null"
          style="cursor: pointer"
        ></i>
      </div>

      <!-- Filter Reset Button -->
      <div
        v-if="isFilterSupplied"
        class="icon-button d-flex justify-content-center text-danger mr-4 ml-2"
        style="width: 36px; min-width: 36px"
        @click="resetAllFilters"
        v-b-tooltip.hover.bottom
        title="Filter zurücksetzen"
      >
        <span class="d-flex align-items-center"><FunnelX :size="20" /></span>
      </div>
    </template>
    <template #toolbar-right>
      <div v-if="allSelectedRows.length > 1" class="d-flex">
        <b-button
          v-if="bulkStatusBearbeitbar"
          variant="secondary"
          size="sm"
          class="dialog-btn-secondary mr-3"
          v-b-modal.bulkStatusBearbeitenModal
        >
          Status bearbeiten
        </b-button>
        <b-button variant="secondary" size="sm" class="dialog-btn-secondary mr-3" v-b-modal.bulkZuweisenModal>
          Neu zuweisen
        </b-button>
      </div>

      <BulkZuweisenModal
        :selectedAufgabenCount="allSelectedRows.length"
        :selectedAufgabenIds="selectedAufgabenIds"
        @neuZugewiesen="neuZugewiesen"
      ></BulkZuweisenModal>
      <BulkStatusBearbeitenModal
        :selectedAufgabenCount="allSelectedRows.length"
        :selectedAufgabenIds="selectedAufgabenIds"
        :referenceAufgabeId="firstSelectedAufgabeId"
        @bulkStatusBearbeitet="onBulkStatusBearbeitet"
      ></BulkStatusBearbeitenModal>
      <div
        class="icon-button d-inline-flex justify-content-center align-middle"
        style="width: 36px"
        v-b-modal.modalAufgabeEdit
        v-if="allSelectedRows.length <= 1"
        v-b-tooltip.hover.bottom
        :title="'Aufgabe erstellen'"
      >
        <span class="d-flex align-items-center"><ListChecks :size="20" /></span>
      </div>
      <AufgabeErstellenModal
        :enableManualTask="true"
        @aufgabeErstellt="gridApi.onFilterChanged()"
      ></AufgabeErstellenModal>
    </template>

    <template #table="{ tableHeight }">
      <AufgabeDetailsModal
        @aufgabeBearbeitet="onAufgabeBearbeitet"
        :currentUserId="currentUserId"
        @rowSelectionChanged="changeRowSelection"
        @updateOverviewData="updateOverviewData"
      ></AufgabeDetailsModal>
      <AufgabeErgebnisFesthaltenModal
        :aufgabeId="ergebnisFesthaltenAufgabeId"
        :aufgabenKategorie="ergebnisFesthaltenKategorie"
        @ergebnisGespeichert="onErgebnisGespeichert"
      />
      <AgGridVue
        :columnDefs="columnDefs"
        :tooltipShowDelay="tooltipShowDelay"
        :tooltipHideDelay="tooltipHideDelay"
        :rowModelType="'serverSide'"
        :statusBar="statusBar"
        :defaultColDef="defaultColDef"
        :style="{ height: tableHeight + 87 + 'px' }"
        :rowSelection="'multiple'"
        :rowMultiSelectWithClick="true"
        :getRowId="getRowId"
        @grid-ready="onGridReady"
        @rowDoubleClicked="onRowDoubleClick"
        @rowSelected="onRowSelected"
        :enableCellTextSelection="true"
        suppressContextMenu
        suppressCellFocus
        class="ag-theme-alpine m-0 p-0"
        id="agGridAufgaben"
      ></AgGridVue>
    </template>
  </FilterOverview>
</template>
<script>
import { AgGridVue } from 'ag-grid-vue';
import 'ag-grid-enterprise';
import FilterOverview from '@/components/common/filter-overview.vue';
import StatusBarComponent from '@/components/flugverfuegbarkeit/status-bar-ag-grid.vue';
import AppSelect from '@/components/common/app-select/app-select.vue';
import { FunnelX, ListChecks } from 'lucide-vue';

import {
  GET_AUFGABEN,
  SET_FILTER_ANSICHT,
  SET_AUFGABE_DETAIL_DATA,
  GET_FILTER_OPTIONS,
  GET_FILTER_OPTIONS_ATTACHMENTS,
  REFETCH_SINGLE_AUFGABE,
  EDIT_STATUS,
  DECREMENT_AUFGABEN_COUNT,
} from '@/core/aufgaben/stores/aufgaben.module';
import { mapState, mapGetters } from 'vuex';
import { format } from 'date-fns';
import AufgabeErstellenModal from '@/components/aufgaben/aufgabe-erstellen-modal.vue';
import AufgabeDetailsModal from '@/components/aufgaben/aufgabe-details-modal.vue';
import AgDateRangePicker from '@/components/flugverfuegbarkeit/ag-date-range-picker.vue';
import NameTooltip from '@/components/aufgaben/name-tooltip.vue';
import BeschreibungTooltip from '@/components/aufgaben/beschreibung-tooltip.vue';
import CustomHeader from '@/components/flugverfuegbarkeit/custom-header.vue';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {
  kategorienSortRanking,
  statusFormatterHtml,
  statusFormatterSmallHtml,
  prioritaetFormatterSmallHtml,
  prioritaetFormatterHtml,
  aufgabenStatusColors,
  grauPillColors,
  pillColorStyle,
} from '@/core/common/helpers/utils.js';
import BulkZuweisenModal from '@/components/aufgaben/bulk-zuweisen-modal.vue';
import BulkStatusBearbeitenModal from '@/components/aufgaben/bulk-status-bearbeiten-modal.vue';
import StatusCellRenderer from '@/components/aufgaben/status-cell-renderer.vue';
import { AUFGABENGRUPPE_REPRU } from '@/core/common/helpers/aufgabengruppen.js';
import AufgabeErgebnisFesthaltenModal from '@/components/aufgaben/aufgabe-ergebnis-festhalten-modal.vue';

export default {
  components: {
    ListChecks,
    FilterOverview,
    AgGridVue,
    StatusBarComponent,
    BulkZuweisenModal,
    BulkStatusBearbeitenModal,
    AufgabeErstellenModal,
    AgDateRangePicker,
    NameTooltip,
    agColumnHeader: CustomHeader,
    AufgabeDetailsModal,
    BeschreibungTooltip,
    AppSelect,
    StatusCellRenderer,
    AufgabeErgebnisFesthaltenModal,
    FunnelX,
  },
  watch: {
    fakeUser() {
      this.gridApi.onFilterChanged();
    },
    ansicht: {
      handler() {
        this.updateSpaltenSichtbarkeit();
      },
    },
  },
  data() {
    return {
      statusFormatterHtml,
      statusFormatterSmallHtml,
      prioritaetFormatterSmallHtml,
      prioritaetFormatterHtml,
      showFakeUserSelect: false,
      fakeUser: null,
      attachmentFilterData: {},
      subnavFilterButtons: [
        'Mir zugewiesen',
        'Von mir erstellt',
        'Von mir beobachtet',
        'Vom Team erstellt',
        'Team',
      ],
      activeFilterButton: 'Mir zugewiesen',
      columnDefs: null,
      tooltipShowDelay: null,
      tooltipHideDelay: null,
      statusBar: null,
      allSelectedRows: [],
      defaultColDef: {
        flex: 1,
        minWidth: 150,
        filter: true,
        sortable: true,
        floatingFilter: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true,
        },
        filterParams: {
          defaultToNothingSelected: true,
        },
        suppressMovable: true,
      },
      rowData: null,
      lastRequest: null,
      isFilterSupplied: false,
      selectedRowID: null,
      // Reihenfolge der Aufgaben-IDs zum Zeitpunkt des öffnens der Detail-Modal
      navigationReihenfolge: [],
      whichTabWasLoaded: null,
      ergebnisFesthaltenAufgabeId: 0,
      ergebnisFesthaltenKategorie: '',
      hasAutoOpenedFromQuery: false,
    };
  },
  created() {
    this.statusBar = {
      statusPanels: [{ statusPanel: 'StatusBarComponent', key: 'statusBarCompKey', align: 'left' }],
    };
    this.columnDefs = [
      {
        field: 'id',
        headerName: 'ID',
        width: 120,
        minWidth: 120,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        headerCheckboxSelection: true,
        checkboxSelection: true,
      },
      {
        headerName: 'Verlinkungen',
        field: 'attachmentAufgabe',
        width: 250,
        minWidth: 250,
        sortable: false,
        tooltipComponent: 'NameTooltip',
        tooltipValueGetter: params => {
          const allReisekuerzel = this.getAllVerlinkungenAufgabe(params.data);
          return allReisekuerzel.length > 0 ? [...new Set(allReisekuerzel)].join(', ') : null;
        },
        cellRenderer: params => {
          const allReisekuerzel = this.getAllVerlinkungenAufgabe(params.data);
          return allReisekuerzel.length > 0
            ? allReisekuerzel.length > 3
              ? [...new Set(allReisekuerzel)].slice(0, 3).join(', ') + '...'
              : [...new Set(allReisekuerzel)].slice(0, 3).join(', ')
            : null;
        },
        filterParams: {
          debounceMs: 1000,
          values: async params => {
            const query = { ...this.lastRequest };
            const values = await this.$store.dispatch(GET_FILTER_OPTIONS_ATTACHMENTS, query);
            const formatValues = Object.values(values).reduce((acc, curr) => {
              if (curr.reiseTermine.length > 0) {
                curr.reiseTermine.forEach(item => {
                  acc[item.reiseterminkuerzel] = item.id;
                });
              }
              if (curr.reisen.length > 0) {
                curr.reisen.forEach(item => {
                  acc[item.reisekuerzel] = item.id;
                });
              }
              if (curr.vorgaenge.length > 0) {
                curr.vorgaenge.forEach(item => {
                  acc[item.id] = item.id;
                });
              }
              if (curr.flugverfuegbarkeiten.length > 0) {
                curr.flugverfuegbarkeiten.forEach(item => {
                  acc[item.reisetermin.reiseterminkuerzel + '_' + item.deutscherAbflughafenIataCode] =
                    item.id;
                });
              }

              return acc;
            }, {});
            this.attachmentFilterData = formatValues;
            this.whichTabWasLoaded = this.activeFilterButton;
            params.success(Object.keys(formatValues));
          },
          comparator: (a, b) => {
            if (!isNaN(parseInt(a)) && !isNaN(parseInt(b))) return 0;
            if (!isNaN(parseInt(a))) {
              return 1;
            }
            if (!isNaN(parseInt(b))) {
              return -1;
            } else return a.localeCompare(b);
          },
        },
        filter: 'agSetColumnFilter',
        floatingFilterComponent: true,
      },
      {
        field: 'kategorie',
        headerName: 'Kategorie',
        width: 250,
        minWidth: 250,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['apply'],
          valueFormatter: params =>
            this.getAllKategorien?.find(item => item.aufgabenKategorie === params.value)?.labelFrontend,
          debounceMs: 1000,
          values: async params => {
            const query = { ...this.lastRequest, attribute: 'kategorie' };
            const kategorie = await this.$store.dispatch(GET_FILTER_OPTIONS, query);
            const uniqueKategorien = [...new Set(kategorie)];
            this.whichTabWasLoaded = this.activeFilterButton;

            params.success(uniqueKategorien);
          },
          comparator: (a, b) => {
            const rankingA = kategorienSortRanking.hasOwnProperty(a.toString())
              ? kategorienSortRanking[a]
              : 5;
            const rankingB = kategorienSortRanking.hasOwnProperty(b.toString())
              ? kategorienSortRanking[b]
              : 5;
            if (rankingA === rankingB) {
              return a.localeCompare(b);
            } else return rankingA > rankingB ? 1 : -1;
          },
        },
        valueFormatter: params => {
          return this.getAllKategorien?.find(item => item.aufgabenKategorie === params.value)?.labelFrontend;
        },
        floatingFilter: true,
      },
      {
        field: 'status',
        width: 160,
        minWidth: 160,
        headerName: 'Status',
        floatingFilter: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['Offen', 'Erledigt', 'InArbeit', 'Pending'],
          debounceMs: 1000,
          valueFormatter: params => (params.value === 'InArbeit' ? 'In Arbeit' : params.value),
          cellRenderer: params => this.statusFormatterSmallHtml[params.value] ?? '(Select all)',
        },
        cellRenderer: 'StatusCellRenderer',
        cellRendererParams: {
          onStatusChange: this.onStatusChangeFromGrid,
        },
        cellStyle: { display: 'flex', 'align-items': 'center' },
      },
      {
        field: 'beschreibung',
        headerName: 'Beschreibung',
        tooltipField: 'beschreibung',
        width: 160,
        minWidth: 160,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        tooltipComponent: 'BeschreibungTooltip',

        cellRenderer: params => {
          if (!params.value) return '';

          const wrapper = document.createElement('span');
          wrapper.style.display = 'block';
          wrapper.style.whiteSpace = 'nowrap';
          wrapper.style.overflow = 'hidden';
          wrapper.style.textOverflow = 'ellipsis';
          //echte htm, einlesen
          const temp = document.createElement('div');
          temp.innerHTML = params.value;

          // Inline eingefügte Bilder werden in der Vorschau durch [Bild] ersetzt
          // Das vollständige Bild bleibt im Tooltip (beim Hover) sichtbar
          temp.querySelectorAll('img').forEach(img => {
            const marker = document.createElement('span');
            marker.textContent = '[Bild]';
            img.replaceWith(marker);
          });

          temp.querySelectorAll('br').forEach(br => {
            br.replaceWith(document.createTextNode(' '));
          });

          // block-Elemente (z.B. <p> aus dem Editor) inline darstellen, damit der
          // Inhalt einzeilig abgeschnitten wird wie bisher
          const blockSelektor = 'p, div, ul, ol, li, table, tbody, thead, tr, td, th';
          temp.querySelectorAll(blockSelektor).forEach(el => {
            el.style.display = 'inline';
          });

          wrapper.innerHTML = temp.innerHTML;

          return wrapper;
        },
      },

      {
        field: 'bearbeiter',
        headerName: 'Bearbeiter/in',
        filter: 'agSetColumnFilter',
        width: 180,
        minWidth: 180,
        tooltipComponent: 'NameTooltip',
        tooltipValueGetter: params => this.$store.getters.getUsernameById(params.value),
        filterParams: {
          keyCreator: params => params.value.value,
          valueFormatter: params => params.value.text,
          debounceMs: 1000,
          comparator: (a, b) => {
            const nameA = a.text;
            const nameB = b.text;
            if (nameA === nameB) return 0;
            return nameA > nameB ? 1 : -1;
          },
          values: async params => {
            const query = { ...this.lastRequest, attribute: 'bearbeiter' };
            const bearbeiter = await this.$store.dispatch(GET_FILTER_OPTIONS, query);
            const uniqueBearbeiter = [...new Set(bearbeiter)];
            this.whichTabWasLoaded = this.activeFilterButton;

            params.success(
              this.zugewiesenePersonOptions.filter(user => uniqueBearbeiter.includes(user.value))
            );
          },
        },
        floatingFilter: true,
        cellRenderer: params => {
          const name = this.$store.getters.getUsernameById(params.value);
          const shortenedName = name
            ?.split(' ')
            .map((subName, i) => {
              if (i == 0) return subName;
              else return subName.substring(0, 1) + '.';
            })
            .join(' ');

          return shortenedName;
        },
      },
      {
        field: 'team',
        headerName: 'Team',
        filter: 'agSetColumnFilter',
        tooltipComponent: 'NameTooltip',
        tooltipValueGetter: params => params.data.team || null,
        filterParams: {
          keyCreator: params => params.value,
          valueFormatter: params => params.value,
          debounceMs: 1000,
          suppressNullFilterValues: true,
          comparator: (a, b) => {
            if (a === b) return 0;
            return a > b ? 1 : -1;
          },
          values: async params => {
            const query = { ...this.lastRequest, attribute: 'team' };
            const teams = await this.$store.dispatch(GET_FILTER_OPTIONS, query);
            const uniqueTeams = [...new Set(teams)].filter(team => team && team.trim() !== '');
            this.whichTabWasLoaded = this.activeFilterButton;

            params.success(uniqueTeams);
          },
        },
        cellRenderer: params => {
          return params.data.team || '';
        },
        floatingFilter: true,
      },
      {
        field: 'faelligkeitsdatum',
        width: 190,
        minWidth: 190,
        headerName: 'Fälligkeitsdatum',
        filter: 'AgDateRangePicker',
        floatingFilterComponent: 'AgDateRangePicker',
        floatingFilterComponentParams: {
          isChild: true,
        },
        cellRenderer: params => {
          const faelligkeitDate = format(new Date(params.value), 'dd.MM.');
          const { werktageBisFaelligkeit } = params.data;
          const label = `${faelligkeitDate} (${werktageBisFaelligkeit ?? '-'})`;
          // Überfällige Aufgaben werden rot dargestellt (wie Status "Offen"), alle anderen grau
          const istUeberfaellig = werktageBisFaelligkeit != null && werktageBisFaelligkeit <= 0;
          const colors = istUeberfaellig ? aufgabenStatusColors.Offen : grauPillColors;
          return `<span class="pill-badge pill-badge--faelligkeit" style="${pillColorStyle(
            colors
          )}">${label}</span>`;
        },
      },
      {
        field: 'prioritaet',
        headerName: 'Priorität',
        filter: 'agSetColumnFilter',
        sortable: true,
        width: 120,
        minWidth: 120,
        filterParams: {
          values: [2, 1, 0],
          debounceMs: 1000,
          valueFormatter: params => (params.value === 0 ? 'Hoch' : params.value === 1 ? 'Mittel' : 'Niedrig'),
          cellRenderer: params => this.prioritaetFormatterSmallHtml[params.value] ?? '(Select all)',
        },
        cellRenderer: params => this.prioritaetFormatterHtml[params.value] ?? '-',
        floatingFilter: true,
      },
      {
        field: 'autor',
        headerName: 'Autor/in',
        floatingFilter: true,
        filter: 'agSetColumnFilter',
        width: 120,
        minWidth: 120,
        tooltipComponent: 'NameTooltip',
        tooltipValueGetter: params =>
          this.$store.getters.getUsernameById(params.value) || 'Automatisch erstellt',
        filterParams: {
          values: params => {
            setTimeout(() => {
              params.success(this.zugewiesenePersonOptions);
            }, 100);
          },
          keyCreator: params => params.value.value,
          valueFormatter: params => params.value.text,
          debounceMs: 1000,
          comparator: (a, b) => {
            const nameA = a.text;
            const nameB = b.text;
            if (nameA === nameB) return 0;
            return nameA > nameB ? 1 : -1;
          },
        },
        cellRenderer: params => {
          if (!params.value) return 'Automatisch erstellt';
          const firstName = this.$store.getters.getUsernameById(params.value)?.split(' ')[0];
          const lastName = this.$store.getters.getUsernameById(params.value)?.split(' ')[1];
          if (!firstName || !lastName) return '';
          return `${firstName} ${lastName.substring(0, 1) + '.'}`;
        },
      },
      {
        field: 'createdDateUtc',
        headerName: 'Erstellungsdatum',
        width: 210,
        minWidth: 210,
        filter: 'AgDateRangePicker',
        floatingFilterComponent: 'AgDateRangePicker',
        floatingFilterComponentParams: {
          isChild: true,
        },
        cellRenderer: params => format(new Date(params.value), 'dd.MM.yyyy HH:mm'),
      },
    ];
    this.tooltipShowDelay = 0;
    this.tooltipHideDelay = 2000;
  },
  mounted() {
    this.addKeyEventListener();
  },
  onUnmounted() {
    this.removeKeyEventListener();
  },
  computed: {
    ...mapState({
      users: state => state.users['users'],
      aufgabenData: state => state.aufgaben.aufgaben,
      ansicht: state => state.aufgaben.filters.ansicht,
      count: state => state.aufgaben.count,
      teamNotificationCount: state => state.aufgaben.teamNotificationCount,
      mirZugewiesenCount: state => state.aufgaben.mirZugewiesenCount,
      teamAnsichtCount: state => state.aufgaben.teamAnsichtCount,
      kategorienConfig: state => state.stateConfig.kategorienConfig,
    }),
    ...mapGetters([
      'currentUserId',
      'getAllKategorien',
      'parsedRoles',
      'getKategorienWithErgebnisFesthalten',
    ]),
    isAdmin() {
      return this.parsedRoles.includes('Admin');
    },
    // Bulk-Statusbearbeitung ist auf Buchhaltung und Admin beschränkt
    isBulkStatusRolle() {
      return this.isAdmin || this.parsedRoles.includes('Buchhaltung');
    },
    isTeamAnsicht() {
      return (
        this.ansicht === 'Team' || this.ansicht === 'Vom Team erstellt' || this.ansicht === 'Team zugewiesen'
      );
    },
    isMeineAnsicht() {
      return (
        this.ansicht === 'Mir zugewiesen' ||
        this.ansicht === 'Von mir erstellt' ||
        this.ansicht === 'Von mir beobachtet'
      );
    },
    zugewiesenePersonOptions() {
      return this.users
        .filter(({ givenName }) => givenName !== null && !givenName.toLowerCase().includes('test'))
        .filter(({ surname }) => surname !== null && !surname.toLowerCase().includes('test'))
        .filter(({ mail }) => mail !== null)
        .map(({ givenName, surname, id }) => ({
          value: id,
          text: givenName + ' ' + surname,
        }));
    },
    zugewiesenePersonOnlyNames() {
      return this.zugewiesenePersonOptions.map(user => user.text);
    },
    selectedAufgabenIds() {
      if (this.allSelectedRows.length > 0) {
        return this.allSelectedRows.map(rowNode => rowNode.data.id);
      }
      return [];
    },
    // Liefert die Id der ersten markierten Aufgabe und dient als Referenz für das Laden der erlaubten Statuswechsel
    firstSelectedAufgabeId() {
      return this.allSelectedRows[0]?.data?.id ?? null;
    },
    // Prüft, ob alle markierten Aufgaben den gleichen Status und die gleiche Kategorie haben
    haveSelectedRowsSameStatusAndKategorie() {
      const { status: firstStatus, kategorie: firstKategorie } = this.allSelectedRows[0].data;
      return this.allSelectedRows.every(
        node => node.data.status === firstStatus && node.data.kategorie === firstKategorie
      );
    },
    // Steuert die Sichtbarkeit des Bulk-Status-Buttons: gleiche Kategorie/Status, keine Ergebnis-Festhalten-Pflicht
    // sowie Beschränkung auf Buchhaltungsaufgaben bzw. Kategorie "Sonstiges" und die Rollen Admin/Buchhaltung
    bulkStatusBearbeitbar() {
      if (!this.isBulkStatusRolle) return false;
      if (!this.haveSelectedRowsSameStatusAndKategorie) return false;
      const firstKategorie = this.allSelectedRows[0].data.kategorie;
      if (this.getKategorienWithErgebnisFesthalten.includes(firstKategorie)) return false;
      if (firstKategorie === 'Sonstiges') return true;
      const config = this.kategorienConfig?.[firstKategorie];
      return !!config?.aufgabengruppen?.includes(AUFGABENGRUPPE_REPRU);
    },
  },
  methods: {
    addKeyEventListener() {
      window.addEventListener('keydown', this.handleKeyPress);
    },
    removeKeyEventListener() {
      window.removeEventListener('keydown', this.handleKeyPress);
    },
    handleKeyPress($event) {
      if ($event.key === 'x') {
        this.showFakeUserSelect = !this.showFakeUserSelect;
      }
    },
    onReload() {
      this.gridApi.onFilterChanged();
    },
    neuZugewiesen() {
      this.gridApi.deselectAll();
      this.gridApi.onFilterChanged();
    },
    // Wendet das Ergebnis eines Bulk-Statuswechsels clientseitig über applyStatusUpdates an
    onBulkStatusBearbeitet(trigger) {
      const triggerToStatus = {
        TriggerOffen: 'Offen',
        TriggerInArbeit: 'InArbeit',
        TriggerPending: 'Pending',
        TriggerErledigt: 'Erledigt',
      };
      const newStatus = triggerToStatus[trigger];

      const selectedRowsSnapshot = [...this.allSelectedRows];
      this.gridApi.deselectAll();

      if (!newStatus) {
        this.gridApi.onFilterChanged();
        return;
      }

      const wirdBearbeiterGeaendert =
        trigger === 'TriggerInArbeit' &&
        selectedRowsSnapshot.some(node => node.data.bearbeiter !== this.currentUserId || !!node.data.team);

      const updates = selectedRowsSnapshot.map(node => ({
        aufgabeId: node.data.id,
        updatedAufgabe: { status: newStatus },
      }));

      this.applyStatusUpdates(updates, wirdBearbeiterGeaendert);
    },
    onRowSelected($event) {
      this.allSelectedRows = this.getAllSelectedRows();
      const statusBarComponent = this.gridApi?.getStatusPanel('statusBarCompKey');
      statusBarComponent?.setMarkedCount(this.allSelectedRows.length);
    },
    getAllSelectedRows() {
      const rowsSelected = [];
      this.gridApi.forEachNode((node, index) => {
        if (node.selected) {
          rowsSelected.push(node);
        }
      });
      return rowsSelected;
    },
    updateOverviewData(updatedAufgabe) {
      this.gridApi.forEachNode(node => {
        if (updatedAufgabe.id === node.data.id) {
          node.updateData(updatedAufgabe);
        }
      });
      if (this.ansicht === 'Von mir beobachtet') {
        this.gridApi.onFilterChanged();
      }
    },
    checkIfFilterSupplied() {
      const usedFilters = Object.keys(this.gridApi?.getFilterModel()).filter(item => item !== 'status');

      const statusFilterValues = this.gridApi.getFilterInstance('status')?.appliedModel?.values;
      const defaultStatusFilterDiffers =
        statusFilterValues &&
        (statusFilterValues.length !== 3 ||
          !statusFilterValues.includes('InArbeit') ||
          !statusFilterValues.includes('Offen') ||
          !statusFilterValues.includes('Pending'));

      this.isFilterSupplied = this.gridApi && (usedFilters.length > 0 || defaultStatusFilterDiffers);
    },
    resetAllFilters() {
      this.gridApi.setFilterModel(null);
      const statusFilterComponent = this.gridApi.getFilterInstance('status');
      statusFilterComponent.setModel({ values: ['Offen', 'InArbeit', 'Pending'] });
      let filterDate1 = this.gridApi.getFilterInstance('faelligkeitsdatum');
      let filterDate2 = this.gridApi.getFilterInstance('createdDateUtc');
      filterDate1.onDateRangeFilterChanged(null);
      filterDate2.onDateRangeFilterChanged(null);
      if (Object.keys(this.$route.query).length > 0) {
        this.$router.replace(this.$route.query);
      }

      this.gridApi.deselectAll();
    },
    changeFilterButton($event) {
      this.resetAllFilters();
      const { id } = $event.target;
      if (this.activeFilterButton !== id) {
        this.activeFilterButton = id;
        this.$store.commit(SET_FILTER_ANSICHT, id);
        this.gridApi.deselectAll();
        this.gridApi.onFilterChanged();
      }
      this.gridApi.refreshHeader();
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;

      this.updateSpaltenSichtbarkeit();

      if (this.$route.query.aufgabenId) {
        const aufgabenIdFilter = this.gridApi?.getFilterInstance('id');
        if (!isNaN(this.$route.query.aufgabenId)) {
          aufgabenIdFilter.setModel({
            filterType: 'text',
            type: 'equal',
            filter: this.$route.query.aufgabenId,
          });
        }
      } else {
        const statusFilterComponent = this.gridApi.getFilterInstance('status');
        statusFilterComponent.setModel({ values: ['Offen', 'InArbeit', 'Pending'] });
      }
      this.gridApi.onFilterChanged();

      const defaultSortModel = [
        { colId: 'faelligkeitsdatum', sort: 'asc', sortIndex: 0 },
        { colId: 'prioritaet', sort: 'desc', sortIndex: 1 },
      ];

      params.columnApi.applyColumnState({ state: defaultSortModel });
      const updateData = () => {
        const fakeServer = this.createFakeServer();
        const datasource = this.createServerSideDatasource(fakeServer);
        params.api.setServerSideDatasource(datasource);
      };
      updateData();
    },
    createServerSideDatasource(server) {
      return {
        getRows: async params => {
          console.log('[Datasource] - rows requested by grid: ', params.request);

          const response = await server.getData(params.request);

          if (response.success) {
            // supply rows for requested block to grid
            params.success({ rowData: response.rows.result.value });
            this.updateStatusBar(this.gridApi?.getDisplayedRowCount());
          } else {
            params.fail();
          }

          if (
            this.$route.query.aufgabenId &&
            !this.hasAutoOpenedFromQuery &&
            response.rows.result.value.length > 0
          ) {
            this.hasAutoOpenedFromQuery = true;
            let rowData;
            this.gridApi.deselectAll();
            this.gridApi.forEachNode(rowNode => {
              if (rowNode.rowIndex === 0) {
                rowNode.setSelected(true, true);
                rowData = rowNode.data;
              }
            });
            this.selectedRowID = 0;
            this.erfasseNavigationReihenfolge();
            this.$store
              .dispatch(REFETCH_SINGLE_AUFGABE, {
                aufgabenId: rowData.id,
              })
              .then(aufgabe => {
                this.$store.commit(SET_AUFGABE_DETAIL_DATA, aufgabe);
              });
            this.$root.$emit('bv::show::modal', 'modalAufgabeDetails');
          }
        },
      };
    },
    updateStatusBar(displayedRowsCount) {
      const statusBarComponent = this.gridApi?.getStatusPanel('statusBarCompKey');
      statusBarComponent?.setRowCount(displayedRowsCount);
      statusBarComponent?.setOdataCount(this.count);
    },
    getAllVerlinkungenAufgabe(data) {
      const verlinkungenFlugverfuegbarkeiten =
        data.flugverfuegbarkeiten.length > 0
          ? data.flugverfuegbarkeiten.map(
              flugverfuegbarkeit =>
                flugverfuegbarkeit.reisetermin.reiseterminkuerzel +
                '_' +
                flugverfuegbarkeit.deutscherAbflughafenIataCode
            )
          : [];
      const verlinkungenReisetermine =
        data.reiseTermine.length > 0
          ? data.reiseTermine.map(reisetermin => reisetermin.reiseterminkuerzel)
          : [];
      const verlinkungenReise = data.reisen.length > 0 ? data.reisen.map(reise => reise.reisekuerzel) : [];
      const verlinkungenVorgaenge =
        data.vorgaenge.length > 0 ? data.vorgaenge.map(vorgang => vorgang.id) : [];
      return [
        ...verlinkungenFlugverfuegbarkeiten,
        ...verlinkungenReise,
        ...verlinkungenReisetermine,
        ...verlinkungenVorgaenge,
      ];
    },
    createFakeServer() {
      return {
        getData: async request => {
          this.checkIfFilterSupplied();
          const req = {
            ...request,
            attachmentFilterData: this.attachmentFilterData,
            fakeUser: this.fakeUser,
          };
          this.lastRequest = req;
          const response = await this.$store.dispatch(GET_AUFGABEN, req);
          await this.refreshColumnSetFilterValues();
          return {
            success: true,
            rows: response,
          };
        },
      };
    },
    refreshColumnSetFilterValues() {
      this.gridApi.getFilterInstance('kategorie')?.refreshFilterValues();
      this.gridApi.getFilterInstance('bearbeiter')?.refreshFilterValues();
      this.gridApi.getFilterInstance('team')?.refreshFilterValues();
      this.gridApi.getFilterInstance('attachmentAufgabe')?.refreshFilterValues();
    },
    async onRowDoubleClick($event) {
      const { data, rowIndex } = $event;
      this.selectedRowID = rowIndex;
      this.erfasseNavigationReihenfolge();
      this.$store
        .dispatch(REFETCH_SINGLE_AUFGABE, {
          aufgabenId: data.id,
        })
        .then(aufgabe => {
          this.$store.commit(SET_AUFGABE_DETAIL_DATA, aufgabe);
        });
      this.$root.$emit('bv::show::modal', 'modalAufgabeDetails');
    },
    // Hält die aktuell angezeigte Reihenfolge der Aufgaben-IDs als Snapshot fest
    erfasseNavigationReihenfolge() {
      const eintraege = [];
      this.gridApi?.forEachNode(node => {
        if (node.data?.id != null && node.rowIndex != null) {
          eintraege.push({ index: node.rowIndex, id: node.data.id });
        }
      });
      this.navigationReihenfolge = eintraege.sort((a, b) => a.index - b.index).map(eintrag => eintrag.id);
    },
    // Sucht den aktuell geladenen Grid-Node (die Tabellenzeile) zu einer Aufgaben-ID
    findeGridNodeNachAufgabeId(aufgabeId) {
      let gefundenerNode = null;
      this.gridApi?.forEachNode(node => {
        if (node.data?.id === aufgabeId) {
          gefundenerNode = node;
        }
      });
      return gefundenerNode;
    },
    changeRowSelection(direction) {
      const reihenfolge = this.navigationReihenfolge;
      if (!reihenfolge || reihenfolge.length === 0) return;

      const offeneAufgabeId = this.$store.state.aufgaben.aufgabeDetail?.id;
      let currentIndex = reihenfolge.indexOf(offeneAufgabeId);
      // Fallback, falls die offene Aufgabe nicht im Snapshot liegt (z.B. nicht geladen)
      if (currentIndex === -1) {
        currentIndex = this.selectedRowID ?? 0;
      }

      // Zielposition bestimmen
      let targetIndex;
      if (direction === 'up') {
        targetIndex = currentIndex !== 0 ? currentIndex - 1 : reihenfolge.length - 1;
      } else if (direction === 'down') {
        targetIndex = currentIndex + 1 < reihenfolge.length ? currentIndex + 1 : 0;
      }

      if (targetIndex == null) return;

      const zielAufgabeId = reihenfolge[targetIndex];
      // Zielzeile sichtbar machen, sofern sie aktuell im Grid geladen ist
      const zielNode = this.findeGridNodeNachAufgabeId(zielAufgabeId);
      if (zielNode?.rowIndex != null) {
        this.gridApi.ensureIndexVisible(zielNode.rowIndex);
      }

      this.$store
        .dispatch(REFETCH_SINGLE_AUFGABE, {
          aufgabenId: zielAufgabeId,
        })
        .then(aufgabe => {
          this.$store.commit(SET_AUFGABE_DETAIL_DATA, aufgabe);
          this.selectedRowID = targetIndex;
        });
    },
    getRowId(params) {
      return String(params.data.id);
    },
    // Wendet das Ergebnis eines oder mehrerer Statuswechsel clientseitig an, ohne einen
    // vollständigen Grid-Refetch auszulösen: passt eine Zeile noch zum aktiven Statusfilter,
    // wird sie lokal aktualisiert; andernfalls wird sie entfernt und der Count entsprechend
    // dekrementiert.
    // Bei Änderungen an Bearbeiter/Team bleibt der Refetch als Fallback bestehen
    applyStatusUpdates(updates, wirdBearbeiterGeaendert = false) {
      if (wirdBearbeiterGeaendert) {
        updates.forEach(({ aufgabeId, updatedAufgabe }) => {
          this.gridApi.forEachNode(node => {
            if (node.data.id === aufgabeId) {
              node.setData({ ...node.data, ...updatedAufgabe });
            }
          });
        });
        this.gridApi.onFilterChanged();
        return;
      }

      const statusValues = this.gridApi.getFilterModel().status?.values;
      const idsToRemove = [];

      updates.forEach(({ aufgabeId, updatedAufgabe }) => {
        const statusPasstZumFilter = !statusValues || statusValues.includes(updatedAufgabe.status);
        if (statusPasstZumFilter) {
          this.gridApi.forEachNode(node => {
            if (node.data.id === aufgabeId) {
              node.setData({ ...node.data, ...updatedAufgabe });
            }
          });
        } else {
          idsToRemove.push(aufgabeId);
        }
      });

      if (idsToRemove.length > 0) {
        //  applyServerSideTransaction ist die offizielle AG-Grid-API, um im Server-Side Row Model gezielt einzelne Zeilen im bereits geladenen Cache zu manipulieren — ohne neuen Server-Call.
        this.gridApi.applyServerSideTransaction({
          remove: idsToRemove.map(id => ({ id })),
        });
        this.$store.commit(DECREMENT_AUFGABEN_COUNT, idsToRemove.length);
        this.updateStatusBar(this.gridApi?.getDisplayedRowCount());
      }
    },
    // Löst den Statuswechsel aus und aktualisiert die betroffene Zeile clientseitig
    async onStatusChangeFromGrid(rowData, trigger) {
      const aufgabeId = rowData.id;

      if (
        trigger === 'TriggerErledigt' &&
        this.getKategorienWithErgebnisFesthalten.includes(rowData.kategorie)
      ) {
        this.ergebnisFesthaltenAufgabeId = aufgabeId;
        this.ergebnisFesthaltenKategorie = rowData.kategorie;
        this.$store.commit(SET_AUFGABE_DETAIL_DATA, rowData);
        this.$bvModal.show('aufgabeErgebnisFesthaltenModal');
        return;
      }

      // Beim Wechsel auf "In Arbeit" setzt das Backend ggf. automatisch den Bearbeiter und
      // entfernt das Team — in diesem Fall muss ein Refetch erfolgen, weil sich dadurch auch die
      // Sichtbarkeit in der aktiven Ansicht ändern kann
      const wirdBearbeiterGeaendert =
        trigger === 'TriggerInArbeit' && (rowData.bearbeiter !== this.currentUserId || !!rowData.team);

      this.$store.commit(SET_AUFGABE_DETAIL_DATA, rowData);
      try {
        const updatedAufgabe = await this.$store.dispatch(EDIT_STATUS, { aufgabeId, trigger });
        this.applyStatusUpdates([{ aufgabeId, updatedAufgabe }], wirdBearbeiterGeaendert);
      } catch (error) {
        console.error('Status konnte nicht geändert werden:', error);
      }
    },
    async onErgebnisGespeichert() {
      const aufgabeId = this.ergebnisFesthaltenAufgabeId;
      try {
        const updatedAufgabe = await this.$store.dispatch(EDIT_STATUS, {
          aufgabeId,
          trigger: 'TriggerErledigt',
        });
        this.applyStatusUpdates([{ aufgabeId, updatedAufgabe }]);
      } catch (error) {
        console.error('Status konnte nicht geändert werden:', error);
      }
    },
    onAufgabeBearbeitet(changedFields = null) {
      if (changedFields !== null) {
        const { id } = changedFields;
        if (id !== null) {
          this.gridApi.forEachNode(rowNode => {
            if (rowNode.data.id === id) {
              const updated = rowNode.data;
              updated.bearbeiter = changedFields.bearbeiter;
              updated.beschreibung = changedFields.beschreibung;
              updated.faelligkeitsdatum = changedFields.faelligkeitsdatum;
              updated.prioritaet = changedFields.prioritaet;
              updated.status = changedFields.status;
              updated.team = changedFields.team;
              rowNode.updateData(updated);
            }
          });
          this.gridApi.onFilterChanged();
        }
      }
    },
    updateSpaltenSichtbarkeit() {
      if (!this.gridColumnApi) return;

      // Alle Ansichten außer folgende:
      const nichtAusgeschlosseneAnsichten = !['Mir zugewiesen', 'Team zugewiesen'].includes(this.ansicht);

      this.gridColumnApi.setColumnVisible('team', nichtAusgeschlosseneAnsichten);
      this.gridColumnApi.setColumnVisible('bearbeiter', this.ansicht !== 'Nicht zugewiesen');
    },
  },
};
</script>
<style scoped>
:deep(.ag-theme-alpine) {
  font-family: 'Poppins';
}
:deep(.ag-theme-alpine .ag-header-cell) {
  font-weight: 500;
  font-size: 14px;
}
:deep(.ag-tabs) {
  min-width: 270px;
}

.icon-button {
  height: 32.52px;
}

.ansicht-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  padding: 4px;
  border-radius: 12px;
  background-color: #e02424;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  vertical-align: middle;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
</style>
